import { Dropdown, Menu } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import { UncontrolledTooltip } from 'reactstrap';
import {
  IconButton,
  ConfirmModal,
  CheckboxInput,
  BlackDeleteIcon as blackDeleteIcon,
  BlackUndoIcon as blackUndoIcon,
  EcgToolbarArrowIconBlack as ToolbarArrowIcon,
} from '@/shared/ui';
import { sizeThumbnailMenu } from '@/entities/ecg';
import PaginationContainer from '../paginationContainer';
import useStripsActionButton from './useStripsActionButton';

type StripsActionButtonProps = {
  isNotEdit?: boolean;
};

/**
 * Renders the auto events toolbar with the delete-strip button, include-in-report and include-strip checkboxes, size dropdown and pagination. Reads config flags and handlers from useStripsActionButton, disables the editing controls while strips load, reveals the reset button only once foundEventChange is set, and raises a modal explaining that at least one Min/Max HR event must remain.
 *
 * @param props - The isNotEdit flag passed down to the pagination container.
 * @returns The auto events toolbar row with its controls, pagination and the prevent-delete modal.
 */
const StripsActionButton = ({ isNotEdit }: StripsActionButtonProps) => {
  const {
    size,
    isLoadingStrip,
    configInfo,
    isOpenPreventDeleteModal,
    setIsOpenPreventDeleteModal,
    onClickItemSize,
    onChangeCheckbox,
    onClickDelete,
    onClickReset,
  } = useStripsActionButton();

  /**
   * Renders the thumbnail-size dropdown of the auto events toolbar. Maps sizeThumbnailMenu into menu items, marks the entry matching the current size as selected, and routes selection to onClickItemSize.
   *
   * @returns The size dropdown with its trigger button showing the current size.
   */
  const renderSizeDropdown = () => {
    const items: any[] = [];
    _.forEach(sizeThumbnailMenu, (x, i) =>
      items.push({
        key: `size-${i}`,
        label: <div className={classNames(x === size ? 'selected' : '')}>{x}</div>,
        value: x,
      }),
    );
    return (
      <Dropdown
        trigger={['click']}
        overlay={<Menu onClick={onClickItemSize} className="thumbnail-size-menu" items={items} />}
      >
        <button id="auto-event-thumbnail-size-btn" className="thumbnail-size-text">
          {`Size: ${size}`}
          <img src={ToolbarArrowIcon} alt="arrow-icon" />
        </button>
      </Dropdown>
    );
  };

  return (
    <div
      id="button-types"
      className="strip-management-content-top-container --min-width-strip-event"
    >
      <div className="toolbar">
        <div id="btnDeleteStripEvents">
          <IconButton
            id="auto-event-delete-strip-btn"
            isOutline
            iconComponent={<img src={blackDeleteIcon} alt="Noise icon" />}
            disabled={configInfo.isDisabled || isLoadingStrip}
            onClick={onClickDelete}
          />
          <UncontrolledTooltip
            className="custom-tooltip"
            target="btnDeleteStripEvents"
            placement="bottom"
            delay={{ show: 0, hide: 0 }}
            hideArrow
          >
            <div className="beat-tooltip">
              Delete strip
              <span className="shortkey">D</span>
            </div>
          </UncontrolledTooltip>
        </div>
        <CheckboxInput
          id="isIncludedToReportStrip"
          checked={!configInfo.isDisabled && configInfo.isIncludedToReport}
          name="isIncludedToReport"
          onChange={(name, value) => {
            onChangeCheckbox(name, value);
          }}
          data={
            {
              label: 'Include event in report',
              value: !configInfo.isIncludedToReport, // pre-existing bug: CheckboxInput's `data.value` is typed string|number
            } as any
          }
          disabled={configInfo.isDisabled || isLoadingStrip}
          className="included-row-checkbox width-186"
          isIndeterminate={!configInfo.isDisabled && configInfo.isIndeterminateToReport}
        />
        <CheckboxInput
          id="isIncludedStripStrip"
          checked={
            !(configInfo.isDisabledStrip || configInfo.isDisabled) && configInfo.isIncludedStrip
          }
          name="isIncludedStrip"
          onChange={(name, value) => {
            onChangeCheckbox(name, value);
          }}
          data={
            {
              label: 'Include strip',
              value: !configInfo.isIncludedStrip, // pre-existing bug: CheckboxInput's `data.value` is typed string|number
            } as any
          }
          disabled={configInfo.isDisabledStrip || configInfo.isDisabled || isLoadingStrip}
          className="included-row-checkbox width-125"
          isIndeterminate={!configInfo.isDisabled && configInfo.isIndeterminateStrip}
        />
        {!!configInfo.foundEventChange && (
          <>
            <div className="btn-type-vertical-line" />
            <IconButton
              id="btnResetStripEvents"
              isOutline
              iconComponent={<img src={blackUndoIcon} alt="Noise icon" />}
              onClick={onClickReset}
              className="reset-btn"
            />
            <UncontrolledTooltip
              className="custom-tooltip"
              target="btnResetStripEvents"
              placement="bottom"
              delay={{ show: 0, hide: 0 }}
              hideArrow
            >
              <div className="beat-tooltip">Reset changes</div>
            </UncontrolledTooltip>
          </>
        )}
      </div>
      {renderSizeDropdown()}
      <div />

      <div className="toolbar-container">
        <PaginationContainer isDisabled={isLoadingStrip} isNotEdit={isNotEdit} />
      </div>

      <ConfirmModal
        isOpen={isOpenPreventDeleteModal}
        onClickRightButton={() => {
          setIsOpenPreventDeleteModal(false);
        }}
        rightButtonName="Okay"
        title="Unable to remove strip"
        question="Unable to remove all events as there must always be at least one Min/Max HR event present."
        isRedBtn={false}
        className="bulk-action-confirm-modal"
        isFilledRightBtn={false}
      />
    </div>
  );
};

export default StripsActionButton;
